import { of, from, BehaviorSubject, Observable, timer, throwError, Subject } from 'rxjs';
import { __assign, __spreadArray } from 'tslib';
import { tap, map, filter, switchMap, skipUntil, delay, first, retryWhen, scan, delayWhen, distinctUntilChanged, mapTo, takeUntil } from 'rxjs/operators';
import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';

var errorCodes = {
    retryLimitsReached: "error.retry-limits-reached"
};
var DesiredConnectionStatus;
(function (DesiredConnectionStatus) {
    DesiredConnectionStatus["disconnected"] = "disconnected";
    DesiredConnectionStatus["connected"] = "connected";
})(DesiredConnectionStatus || (DesiredConnectionStatus = {}));
var ConnectionStatus;
(function (ConnectionStatus) {
    ConnectionStatus["disconnected"] = "disconnected";
    ConnectionStatus["connecting"] = "connecting";
    ConnectionStatus["connected"] = "connected";
})(ConnectionStatus || (ConnectionStatus = {}));
var InternalConnectionStatus;
(function (InternalConnectionStatus) {
    InternalConnectionStatus["disconnected"] = "disconnected";
    InternalConnectionStatus["ready"] = "ready";
    InternalConnectionStatus["connected"] = "connected";
})(InternalConnectionStatus || (InternalConnectionStatus = {}));

function random(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

function getReconnectionDelay(retryOptions, retryCount) {
    if (retryOptions.customStrategy) {
        return retryOptions.customStrategy(retryOptions, retryCount);
    }
    if (retryOptions.randomBackOffStrategy) {
        return randomBackOffStrategyDelay(retryOptions.randomBackOffStrategy, retryCount);
    }
    if (retryOptions.backOffStrategy) {
        return backOffStrategyDelay(retryOptions.backOffStrategy, retryCount);
    }
    if (retryOptions.randomStrategy) {
        return randomStrategyDelay(retryOptions.randomStrategy);
    }
    return defaultStrategy(retryCount);
}
function randomStrategyDelay(randomStrategy) {
    return random(randomStrategy.min, randomStrategy.max) * randomStrategy.intervalMs;
}
function randomBackOffStrategyDelay(randomStrategy, retryCount) {
    var maxValue = Math.min(retryCount, randomStrategy.max);
    maxValue = randomStrategy.min >= maxValue ? (randomStrategy.min + 2) : maxValue;
    return random(randomStrategy.min, maxValue) * randomStrategy.intervalMs;
}
function backOffStrategyDelay(backOffStrategy, retryCount) {
    return Math.min(retryCount * backOffStrategy.delayRetriesMs, backOffStrategy.maxDelayRetriesMs);
}
function defaultStrategy(retryCount) {
    return randomBackOffStrategyDelay({ min: 3, max: 15, intervalMs: 1000 }, retryCount);
}

function buildQueryString(data) {
    var queryString = "";
    if (!data) {
        return queryString;
    }
    // tslint:disable-next-line:forin
    for (var key in data) {
        queryString += queryString === "" ? "?" : "&";
        queryString += key + "=" + data[key];
    }
    return queryString;
}

function emptyNext() {
    return of(undefined);
}

var errorReasonName = "error";
var disconnectedState = Object.freeze({ status: ConnectionStatus.disconnected });
var connectedState = Object.freeze({ status: ConnectionStatus.connected });
var connectingState = Object.freeze({ status: ConnectionStatus.connecting });
// todo: rename HubClient?
var HubConnection = /** @class */ (function () {
    function HubConnection(connectionOption) {
        var _this = this;
        this._connectionState$ = new BehaviorSubject(disconnectedState);
        this.desiredState$ = new BehaviorSubject(DesiredConnectionStatus.disconnected);
        this.internalConnStatus$ = new BehaviorSubject(InternalConnectionStatus.disconnected);
        this.connectionBuilder = new HubConnectionBuilder();
        this._destroy$ = new Subject();
        this.waitUntilConnect$ = this.connectionState$.pipe(distinctUntilChanged(function (x, y) { return x.status === y.status; }), skipUntil(this.connectionState$.pipe(filter(function (x) { return x.status === ConnectionStatus.connected; }))));
        this._key = connectionOption.key;
        this.source = "[" + connectionOption.key + "] HubConnection ::";
        this.retry = connectionOption.options && connectionOption.options.retry ? connectionOption.options.retry : {};
        this.hubConnectionOptions$ = new BehaviorSubject(connectionOption);
        var connection$ = this.hubConnectionOptions$.pipe(map(function (connectionOpts) { return [connectionOpts, _this.internalConnStatus$.value]; }), switchMap(function (_a) {
            var connectionOpts = _a[0], prevConnectionStatus = _a[1];
            return _this._disconnect().pipe(map(function () { return _this.mergeConnectionData(connectionOpts); }), map(buildQueryString), tap(function (queryString) {
                var _a;
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                _this.connectionBuilder.withUrl("" + connectionOpts.endpointUri + queryString, connectionOpts.options);
                if (connectionOpts.protocol) {
                    _this.connectionBuilder = _this.connectionBuilder.withHubProtocol(connectionOpts.protocol);
                }
                _this.hubConnection = _this.connectionBuilder.build();
                (_a = connectionOpts.configureSignalRHubConnection) === null || _a === void 0 ? void 0 : _a.call(connectionOpts, _this.hubConnection);
                _this.hubConnection.onclose(function (err) {
                    _this.internalConnStatus$.next(InternalConnectionStatus.disconnected);
                    if (err) {
                        console.error(_this.source + " session disconnected with errors", { name: err.name, message: err.message });
                        _this._connectionState$.next({
                            status: ConnectionStatus.disconnected,
                            reason: errorReasonName,
                            data: { name: err.name, message: err.message }
                        });
                    }
                    else {
                        console.warn(_this.source + " session disconnected");
                        _this._connectionState$.next(disconnectedState);
                    }
                });
            }), tap(function () { return _this.internalConnStatus$.next(InternalConnectionStatus.ready); }), filter(function () { return prevConnectionStatus === InternalConnectionStatus.connected; }), switchMap(function () { return _this.openConnection(); }));
        }), takeUntil(this._destroy$));
        var desiredDisconnected$ = this.desiredState$.pipe(filter(function (status) { return status === DesiredConnectionStatus.disconnected; }), 
        // tap(status => console.warn(">>>> [desiredDisconnected$] desired disconnected", { internalConnStatus$: this.internalConnStatus$.value, desiredStatus: status })),
        tap(function () {
            if (_this._connectionState$.value.status !== ConnectionStatus.disconnected) {
                // console.warn(">>>> [desiredDisconnected$] _disconnect");
                // note: ideally delayWhen disconnect first, though for some reason obs not bubbling
                _this._disconnect();
            }
        }), tap(function () { return _this._connectionState$.next(disconnectedState); }), takeUntil(this._destroy$));
        var reconnectOnDisconnect$ = this._connectionState$.pipe(
        // tap(x => console.warn(">>>> _connectionState$ state changed", x)),
        filter(function (x) { return x.status === ConnectionStatus.disconnected && x.reason === errorReasonName; }), 
        // tap(x => console.warn(">>>> reconnecting...", x)),
        switchMap(function () { return _this.connect(); }), takeUntil(this._destroy$));
        [
            desiredDisconnected$,
            reconnectOnDisconnect$,
            connection$
        ].forEach(function (x) { return x.subscribe(); });
    }
    Object.defineProperty(HubConnection.prototype, "connectionState$", {
        get: function () { return this._connectionState$.asObservable(); },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(HubConnection.prototype, "key", {
        get: function () { return this._key; },
        enumerable: false,
        configurable: true
    });
    HubConnection.prototype.connect = function (data) {
        var _this = this;
        // console.warn("[connect] init", data);
        this.desiredState$.next(DesiredConnectionStatus.connected);
        if (this.internalConnStatus$.value === InternalConnectionStatus.connected) {
            console.warn(this.source + " session already connected");
            return emptyNext();
        }
        if (data) {
            this.setData(data);
        }
        // todo: refactor this and use subject only instead
        return emptyNext().pipe(switchMap(function () { return _this.internalConnStatus$.pipe(tap(function (x) {
            if (x === InternalConnectionStatus.disconnected) {
                _this.hubConnectionOptions$.next(_this.hubConnectionOptions$.value);
            }
        }), skipUntil(_this.internalConnStatus$.pipe(filter(function (x) { return x === InternalConnectionStatus.ready; }))), first()); }), switchMap(function () { return _this.openConnection(); }));
    };
    HubConnection.prototype.disconnect = function () {
        // console.warn("[disconnect] init");
        this.desiredState$.next(DesiredConnectionStatus.disconnected);
        return this.untilDisconnects$();
    };
    HubConnection.prototype.setData = function (getData) {
        var connection = this.hubConnectionOptions$.value;
        connection.getData = getData;
        this.hubConnectionOptions$.next(connection);
    };
    HubConnection.prototype.on = function (methodName) {
        var _this = this;
        var stream$ = new Observable(function (observer) {
            var updateEvent = function (latestValue) { return observer.next(latestValue); };
            _this.hubConnection.on(methodName.toString(), updateEvent);
            return function () { return _this.hubConnection.off(methodName.toString(), updateEvent); };
        });
        return this.activateStreamWithRetry(stream$);
    };
    HubConnection.prototype.stream = function (methodName) {
        var _this = this;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var stream$ = new Observable(function (observer) {
            var _a;
            (_a = _this.hubConnection).stream.apply(_a, __spreadArray([methodName.toString()], args)).subscribe({
                closed: false,
                next: function (item) { return observer.next(item); },
                error: function (err) {
                    if (err && err.message !== "Invocation cancelled due to connection being closed.") {
                        observer.error(err);
                    }
                },
                complete: function () { return observer.complete(); }
            });
            return function () {
                emptyNext().pipe(delay(1), // workaround - when connection disconnects, stream errors fires before `signalr.onClose`
                filter(function () { return _this.internalConnStatus$.value === InternalConnectionStatus.connected; }), switchMap(function () { return _this.send.apply(_this, __spreadArray(["StreamUnsubscribe", methodName], args)); })).subscribe();
            };
        });
        return this.activateStreamWithRetry(stream$);
    };
    HubConnection.prototype.send = function (methodName) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return from((_a = this.hubConnection).send.apply(_a, __spreadArray([methodName.toString()], args)));
    };
    HubConnection.prototype.invoke = function (methodName) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return from((_a = this.hubConnection).invoke.apply(_a, __spreadArray([methodName.toString()], args)));
    };
    HubConnection.prototype.dispose = function () {
        this.disconnect();
        this.desiredState$.complete();
        this._connectionState$.complete();
        this.internalConnStatus$.complete();
        this._destroy$.next();
        this._destroy$.complete();
    };
    HubConnection.prototype._disconnect = function () {
        // console.warn("[_disconnect] init", this.internalConnStatus$.value, this._connectionState$.value);
        return this._connectionState$.value.status !== ConnectionStatus.disconnected
            ? from(this.hubConnection.stop())
            : emptyNext();
    };
    HubConnection.prototype.untilDisconnects$ = function () {
        return this.connectionState$.pipe(first(function (state) { return state.status !== ConnectionStatus.connected; }), mapTo(undefined));
    };
    HubConnection.prototype.untilDesiredDisconnects$ = function () {
        return this.desiredState$.pipe(first(function (state) { return state === DesiredConnectionStatus.disconnected; }), mapTo(undefined));
    };
    HubConnection.prototype.openConnection = function () {
        var _this = this;
        // console.warn("[openConnection]");
        return emptyNext().pipe(
        // tap(x => console.warn(">>>> openConnection - attempting to connect", x)),
        tap(function () { return _this._connectionState$.next(connectingState); }), switchMap(function () { return from(_this.hubConnection.start()); }), 
        // tap(x => console.warn(">>>> [openConnection] - connection established", x)),
        retryWhen(function (errors) { return errors.pipe(scan(function (errorCount) { return ++errorCount; }, 0), delayWhen(function (retryCount) {
            if (_this.retry.maximumAttempts && retryCount > _this.retry.maximumAttempts) {
                return throwError(new Error(errorCodes.retryLimitsReached));
            }
            var nextRetryMs = getReconnectionDelay(_this.retry, retryCount);
            // tslint:disable-next-line:no-console
            console.debug(_this.source + " connect :: retrying", { retryCount: retryCount, maximumAttempts: _this.retry.maximumAttempts, nextRetryMs: nextRetryMs });
            _this._connectionState$.next({
                status: ConnectionStatus.connecting,
                reason: "reconnecting",
                data: { retryCount: retryCount, maximumAttempts: _this.retry.maximumAttempts, nextRetryMs: nextRetryMs }
            });
            // todo: refactor with subject instead e.g.
            _this.hubConnectionOptions$.next(_this.hubConnectionOptions$.value);
            return timer(nextRetryMs);
        })); }), tap(function () { return _this.internalConnStatus$.next(InternalConnectionStatus.connected); }), tap(function () { return _this._connectionState$.next(connectedState); }), takeUntil(this.untilDesiredDisconnects$()));
    };
    HubConnection.prototype.activateStreamWithRetry = function (stream$) {
        var _this = this;
        return this.waitUntilConnect$.pipe(switchMap(function () { return stream$.pipe(retryWhen(function (errors) { return errors.pipe(delay(1), // workaround - when connection disconnects, stream errors fires before `signalr.onClose`
        delayWhen(function () { return _this.waitUntilConnect$; })); })); }));
    };
    HubConnection.prototype.mergeConnectionData = function (options) {
        var data = {};
        if (options.defaultData) {
            data = options.defaultData();
        }
        if (options.getData) {
            var specificData = options.getData();
            data = __assign(__assign({}, data), specificData);
        }
        return data;
    };
    return HubConnection;
}());

var HubConnectionFactory = /** @class */ (function () {
    function HubConnectionFactory() {
        this.source = "HubConnectionFactory ::";
        this.hubConnections = {};
    }
    HubConnectionFactory.prototype.create = function () {
        var connectionOptions = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            connectionOptions[_i] = arguments[_i];
        }
        for (var _a = 0, connectionOptions_1 = connectionOptions; _a < connectionOptions_1.length; _a++) {
            var connectionOption = connectionOptions_1[_a];
            if (!connectionOption.key) {
                throw new Error(this.source + " create :: connection key not set");
            }
            if (!connectionOption.endpointUri) {
                throw new Error(this.source + " create :: connection endpointUri not set for " + connectionOption.key);
            }
            if (!connectionOption.options) {
                connectionOption.options = {
                    transport: HttpTransportType.WebSockets
                };
            }
            else if (!connectionOption.options.transport) {
                connectionOption.options.transport = HttpTransportType.WebSockets;
            }
            this.hubConnections[connectionOption.key] = new HubConnection(connectionOption);
        }
        return this;
    };
    HubConnectionFactory.prototype.get = function (key) {
        var hub = this.hubConnections[key];
        if (hub) {
            return hub;
        }
        throw new Error(this.source + " get :: connection key not found '" + key + "'");
    };
    HubConnectionFactory.prototype.remove = function (key) {
        var hub = this.hubConnections[key];
        if (hub) {
            hub.dispose();
            delete this.hubConnections[key];
        }
    };
    HubConnectionFactory.prototype.connectAll = function () {
        // tslint:disable-next-line:forin
        for (var hubKey in this.hubConnections) {
            var hub = this.hubConnections[hubKey];
            hub.connect();
        }
    };
    HubConnectionFactory.prototype.disconnectAll = function () {
        // tslint:disable-next-line:forin
        for (var hubKey in this.hubConnections) {
            var hub = this.hubConnections[hubKey];
            hub.disconnect();
        }
    };
    return HubConnectionFactory;
}());

var VERSION = "3.1.0";

export { HubConnection, HubConnectionFactory, ConnectionStatus, errorCodes, VERSION };
//# sourceMappingURL=index.js.map
